import { get } from '@rover/react-lib/src/apis/breeds.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import type { AutocompleteSelectOption, Breed } from '@rover/types';

export type State = {
  catBreeds: Breed[];
  dogBreeds: Breed[];
  errorMessage: string;
};

type Actions = {
  updateCatBreeds: ActionCreator<{
    catBreeds: Breed[];
  }>;
  updateDogBreeds: ActionCreator<{
    dogBreeds: Breed[];
  }>;
  fetchAllBreedsFailure: ActionCreator<string>;
};

type Effects = {
  fetchCatBreeds: EffectCreator<void>;
  fetchDogBreeds: EffectCreator<void>;
  fetchAllBreeds: EffectCreator<void>;
};

type Selectors = {
  getErrorMessage: SelectorCreator<string>;
  getCatBreeds: SelectorCreator<Breed[]>;
  getCatBreedNames: SelectorCreator<string[]>;
  getDogBreeds: SelectorCreator<Breed[]>;
  getDogBreedNames: SelectorCreator<string[]>;
  getDogBreedOptions: SelectorCreator<AutocompleteSelectOption[]>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'breeds',
  initialState: {
    catBreeds: [],
    dogBreeds: [],
    errorMessage: '',
  },
  actions: {
    updateCatBreeds:
      ({ catBreeds }) =>
      (state): State => ({ ...state, catBreeds }),
    updateDogBreeds:
      ({ dogBreeds }) =>
      (state): State => ({ ...state, dogBreeds }),
    fetchAllBreedsFailure: (errorMessage) => (state) => ({
      ...state,
      errorMessage,
    }),
  },
  effects: {
    fetchCatBreeds:
      () =>
      async (dispatch, getState, duckActions): Promise<void> => {
        const catBreeds: Breed[] = await get({
          catBreeds: true,
        });
        dispatch(
          duckActions.updateCatBreeds({
            catBreeds,
          })
        );
      },
    fetchDogBreeds:
      () =>
      async (dispatch, getState, duckActions): Promise<void> => {
        const dogBreeds: Breed[] = await get();
        dispatch(
          duckActions.updateDogBreeds({
            dogBreeds,
          })
        );
      },
    fetchAllBreeds:
      () =>
      async (dispatch, getState, duckActions): Promise<void> => {
        try {
          const [catBreeds, dogBreeds] = await Promise.all([get({ catBreeds: true }), get()]);
          dispatch(
            duckActions.updateCatBreeds({
              catBreeds,
            })
          );
          dispatch(
            duckActions.updateDogBreeds({
              dogBreeds,
            })
          );
        } catch (error: any) {
          dispatch(duckActions.fetchAllBreedsFailure(error.message));
        }
      },
  },
  selectors: {
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.errorMessage),
    getCatBreeds: (getState, createSelector) => createSelector([getState], (s) => s.catBreeds),
    getCatBreedNames: (getState, createSelector, duckSelectors) =>
      createSelector([duckSelectors.getCatBreeds], (b: Breed[]) =>
        b.map((breed: Breed) => breed.name)
      ),
    getDogBreeds: (getState, createSelector) => createSelector([getState], (s) => s.dogBreeds),
    getDogBreedNames: (getState, createSelector, duckSelectors) =>
      createSelector([duckSelectors.getDogBreeds], (b: Breed[]) =>
        b.map((breed: Breed) => breed.name)
      ),
    getDogBreedOptions: (getState, createSelector, duckSelectors) =>
      createSelector([duckSelectors.getDogBreeds], (breeds: Breed[]) =>
        breeds.map((breed: Breed) => ({
          title: breed.name,
          value: breed.slug,
          id: breed.slug,
        }))
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
